import React from 'react';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { ResponsiveImage } from '../../components/';

import css from './Logo.module.css';

export const HEIGHT_24 = 24;
export const HEIGHT_36 = 36;
export const HEIGHT_48 = 48;
export const HEIGHT_60 = 60;
export const HEIGHT_72 = 72;

const HEIGHT_OPTIONS = [
  HEIGHT_24,
  HEIGHT_36,
  HEIGHT_48,
  HEIGHT_60,
  HEIGHT_72,
];

// logoSettings property supports only 3 types at this point
const isValidLogoSettings = settings =>
  settings?.format === 'image' && HEIGHT_OPTIONS.includes(settings?.height);
const isImageAsset = logo => logo?.type === 'imageAsset';

// Each type can have multiple image variants
const getVariantNames = variantsObj => {
  return Object.keys(variantsObj) || [];
};

// Variant data contains width and height among other things
// The width is needed for sizes attribute of responsive logo imgs
const getVariantData = variants => {
  // This assume that "scaled" variant exists
  // If other variants are introduced, this setup might need some tuning.
  return variants['scaled'];
};

// We have maximum heights for each logo type. It's enforced through classes
const getHeightClassName = (logoHeight, css) => {
  if (logoHeight === HEIGHT_24) {
    return css.logo24;
  } else if (logoHeight === HEIGHT_36) {
    return css.logo36;
  } else if (logoHeight === HEIGHT_48) {
    return css.logo48;
  } else if (logoHeight === HEIGHT_60) {
    return css.logo60;
  } else if (logoHeight === HEIGHT_72) {
    return css.logo72;
  }
  return css.logoDefault;
};

export const LogoComponent = props => {
  const {
    className,
    logoImageClassName,
    layout,
    marketplaceName,
    logoImageDesktop,
    logoImageMobile,
    logoSettings,
    ...rest
  } = props;

  const hasValidLogoSettings = isValidLogoSettings(logoSettings);
  const logoClasses = className || css.root;
  const logoImageClasses = classNames(
    logoImageClassName || css.logo,
    getHeightClassName(logoSettings?.height, css)
  );

  const targetHeight = logoSettings?.height === HEIGHT_60 ? HEIGHT_60 : logoSettings?.height;

  // Logo from hosted asset
  if (isImageAsset(logoImageDesktop) && hasValidLogoSettings && layout === 'desktop') {
    const variants = logoImageDesktop.attributes.variants;
    const variantNames = getVariantNames(variants);
    // Ensure 'scaled' variant exists or adjust logic to pick a suitable one
    const { width: scaledWidth } = getVariantData(variants) || { width: 'auto' };
    const imageWidth = targetHeight === HEIGHT_60 && scaledWidth !== 'auto' 
      ? Math.round(scaledWidth * (targetHeight / (variants?.scaled?.height || targetHeight))) 
      : scaledWidth;


    return (
      <div className={logoClasses} style={targetHeight === HEIGHT_60 && imageWidth !== 'auto' ? { width: `${imageWidth}px` } : {}}>
        <ResponsiveImage
          rootClassName={logoImageClasses}
          alt={marketplaceName}
          image={logoImageDesktop}
          variants={variantNames}
          sizes={imageWidth !== 'auto' ? `${imageWidth}px` : undefined}
          width={imageWidth !== 'auto' ? imageWidth : undefined}
          height={targetHeight} // Pass targetHeight
        />
      </div>
    );
  } else if (isImageAsset(logoImageMobile) && hasValidLogoSettings && layout === 'mobile') {
    const variants = logoImageMobile.attributes.variants;
    const variantNames = getVariantNames(variants);
    const { width: scaledWidth } = getVariantData(variants) || { width: 'auto' };
    const imageWidth = targetHeight === HEIGHT_60 && scaledWidth !== 'auto'
      ? Math.round(scaledWidth * (targetHeight / (variants?.scaled?.height || targetHeight)))
      : scaledWidth;

    const sizes =
      imageWidth !== 'auto' && imageWidth <= 188 ? `${imageWidth}px` : `(max-width: 500px) calc(100vw - 132px), ${imageWidth !== 'auto' ? imageWidth + 'px' : '100%'})`;
    return (
      <div className={logoClasses}>
        <ResponsiveImage
          rootClassName={logoImageClasses}
          alt={marketplaceName}
          image={logoImageMobile}
          variants={variantNames}
          sizes={sizes}
          width={imageWidth !== 'auto' ? imageWidth : undefined}
          height={targetHeight} // Pass targetHeight
        />
      </div>
    );
  } else if (layout === 'desktop') {
    return (
      <div className={logoClasses}>
        <img className={logoImageClasses} src={logoImageDesktop} alt={marketplaceName} {...rest} style={{ height: targetHeight ? `${targetHeight}px` : 'auto', width: 'auto' }} />
      </div>
    );
  }

  return (
    <div className={logoClasses}>
      <img className={logoImageClasses} src={logoImageMobile} alt={marketplaceName} {...rest} style={{ height: targetHeight ? `${targetHeight}px` : 'auto', width: 'auto' }} />
    </div>
  );
};

/**
 * This component returns a logo
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {('desktop' | 'mobile')} props.layout
 * @param {string?} props.alt alt text for logo image
 * @returns {JSX.Element} logo component
 */
const Logo = props => {
  const config = useConfiguration();
  const { layout = 'desktop', ...rest } = props;
  // NOTE: logo images are set in hosted branding.json asset or src/config/brandingConfig.js
  const { logoImageDesktop, logoImageMobile, logoSettings } = config.branding;

  return (
    <LogoComponent
      {...rest}
      layout={layout}
      logoImageDesktop={logoImageDesktop}
      logoImageMobile={logoImageMobile}
      logoSettings={logoSettings}
      marketplaceName={config.marketplaceName}
    />
  );
};

export default Logo;
